/**
 * eventDelta — airtight merge of a `/events?since=N` delta into a cached log.
 *
 * The server's event log is APPEND-ONLY: an event at index i never changes, only
 * new events append. So `cached[0..N-1]` is always a stable prefix, and the
 * server's `?since=N` response is exactly the contiguous suffix `events[N..]`.
 * Appending it can never create a gap.
 *
 * The merge is paranoid about completeness: it only appends when the delta's
 * `since` lines up exactly with the cache length, and it flags `needsResync`
 * whenever the merged array can't be PROVEN to be the whole gap-free log
 * (length ≠ eventCount, or an offset that doesn't align). On resync the caller
 * re-fetches the full log (`since=0`). Net effect: we either have every event
 * contiguously, or we throw the cache away and refetch — never a silent gap.
 */

import type { GameEvent, GameEventsResponse } from '@warcarp/api-types'

export interface EventMerge {
  events: GameEvent[]
  /** True when the result can't be proven complete — caller must refetch full. */
  needsResync: boolean
}

export function mergeEventDelta(
  cached: readonly GameEvent[],
  resp: Pick<GameEventsResponse, 'events' | 'eventCount' | 'since'>,
): EventMerge {
  const since = resp.since ?? 0
  const delta = (resp.events ?? []) as GameEvent[]

  // Full payload (since 0, or a legacy server without `since`): authoritative.
  if (since === 0) {
    return { events: delta, needsResync: delta.length !== resp.eventCount }
  }

  // Delta must start exactly where our cache ends, or we can't safely append.
  if (since !== cached.length) {
    return { events: cached as GameEvent[], needsResync: true }
  }

  const merged = [...cached, ...delta]
  // Must reach the authoritative total — otherwise we'd be missing events.
  return { events: merged, needsResync: merged.length !== resp.eventCount }
}
